import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { useTheme } from "@mui/material/styles";

const WirelessChannelInterfaceChart = ({ data = [] }) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstanceRef.current) {
      chartInstanceRef.current = echarts.init(chartRef.current);
    }

    const chart = chartInstanceRef.current;

    const chartData = Array.isArray(data)
      ? data.map(([label, value]) => ({
          label,
          value: Number(value) || 0,
        }))
      : [];

    const maxValue = Math.max(...chartData.map((item) => item.value), 0);
    const yAxisMax = Math.ceil(maxValue / 10) * 10 || 10;
    const yAxisInterval = Math.ceil(yAxisMax / 3);

    const option = {
      grid: {
        top: 20,
        left: 40,
        right: 20,
        bottom: 50,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartData.map((item) => item.label),
        axisLine: {
          lineStyle: {
            color: theme.palette.primary.greyThread,
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
          formatter: (value) => {
            if (!value) return "";

            if (value.startsWith("--")) {
              return `< ${value.replace("--", "-")} dBm`;
            }

            if (value.endsWith("-")) {
              return `> ${value.slice(0, -1)} dBm`;
            }

            const match = value.match(/-?\d+/g);
            if (match && match.length === 2) {
              const [a, b] = match.map(Number);
              return `${Math.min(a, b)} ~ ${Math.max(a, b)} dBm`;
            }

            return value;
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        interval: yAxisInterval,
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        axisPointer: {
          type: "shadow",
        },
        formatter: (params) => {
          const item = params?.[0];
          if (!item) return "";

          return `${item.name}<br />Neighboring SSIDs: ${item.value}`;
        },
      },
      series: [
        {
          type: "bar",
          data: chartData.map((item) => item.value),
          barWidth: 60,
          itemStyle: {
            color: theme.palette.primary.mainHover,
          },
        },
      ],
    };

    chart.setOption(option, true);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [data]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  return <div ref={chartRef} style={{ width: "100%", height: "260px" }} />;
};

export default WirelessChannelInterfaceChart;
